// Zod contracts for the workflow module (C9, M3). Every request and response schema of
// /api/workflow/* and /api/admin/workflow/* lives here (architecture/architecture.md Section 11).
// Stages are fixed and statuses are data (root 4.7, data-model 7): a status keeps its ID across
// versions, and its name is the name it has in the version being shown.
import { z } from 'zod';
import { CODE_LISTS } from '../constants/code-lists.js';
import { STATUS_SYSTEM_CODES, WORKFLOW_CODES } from '../constants/workflow.js';

/** A workflow, by the code of the ticket type it serves (poc CT-004): `bug` or `feature_request`. */
export const WorkflowCodeSchema = z.enum([WORKFLOW_CODES.bug, WORKFLOW_CODES.featureRequest]);
/** `?workflow=` on the workflow routes; default `bug` (module-3 Section 6.1). */
export const WorkflowQuerySchema = z.object({ workflow: WorkflowCodeSchema.default(WORKFLOW_CODES.bug) });
export const StageCodeSchema = z.enum(CODE_LISTS.stage_code);
export const StatusSystemCodeSchema = z.enum(STATUS_SYSTEM_CODES);
/**
 * A move can be limited only to the Owner and Engineer roles (REQ-7.9). poc CT-008: every Editor and Admin holds
 * `owner`, so the editor offers one choice, "Editors only", stored as both codes; `engineer` stays because
 * published versions (immutable) and publish check 5 compare against it.
 */
export const TRANSITION_ROLE_CODES = ['owner', 'engineer'] as const;
export const TransitionRoleSchema = z.enum(TRANSITION_ROLE_CODES);
export type TransitionRole = z.infer<typeof TransitionRoleSchema>;

const Uuid = z.uuid();
const IsoTime = z.string();
const StatusName = z.string().trim().min(1).max(100);

export const WorkflowStageSchema = z.object({
  id: Uuid,
  code: StageCodeSchema,
  name: z.string(),
  sortOrder: z.number().int(),
  isOpen: z.boolean(),
  isFinished: z.boolean(),
  /** The stage's default status: fixed, reordering doesn't change it (REQ-7.1). */
  defaultStatusId: Uuid,
});
export type WorkflowStage = z.infer<typeof WorkflowStageSchema>;

export const WorkflowStatusSchema = z.object({
  id: Uuid,
  stageId: Uuid,
  stageCode: StageCodeSchema,
  /** The status's name in this version. */
  name: z.string(),
  sortOrder: z.number().int(),
  systemCode: StatusSystemCodeSchema.nullable(),
  isStageDefault: z.boolean(),
  shownToReporters: z.boolean(),
});
export type WorkflowStatus = z.infer<typeof WorkflowStatusSchema>;

export const WorkflowTransitionSchema = z.object({
  id: Uuid,
  fromStatusId: Uuid,
  toStatusId: Uuid,
  requiresReason: z.boolean(),
  /** Empty: no role limit beyond the requirements' own rules. */
  roles: z.array(TransitionRoleSchema),
  protectedMoveId: Uuid.nullable(),
});
export type WorkflowTransition = z.infer<typeof WorkflowTransitionSchema>;

export const WorkflowVersionSchema = z.object({
  id: Uuid,
  workflowId: Uuid,
  /** The workflow this version belongs to: a version knows its workflow (poc CT-004). */
  workflowCode: WorkflowCodeSchema,
  /** Null for a draft. */
  versionNumber: z.number().int().nullable(),
  state: z.enum(CODE_LISTS.workflow_version_state),
  changeSummary: z.string().nullable(),
  createdByPersonId: Uuid.nullable(),
  createdAt: IsoTime,
  publishedByPersonId: Uuid.nullable(),
  publishedAt: IsoTime.nullable(),
  stages: z.array(WorkflowStageSchema),
  statuses: z.array(WorkflowStatusSchema),
  transitions: z.array(WorkflowTransitionSchema),
});
export type VersionView = z.infer<typeof WorkflowVersionSchema>;
/** A draft has the same shape as a version (state `draft`, no number). */
export type DraftView = VersionView;

export const VersionSummarySchema = z.object({
  id: Uuid,
  versionNumber: z.number().int(),
  changeSummary: z.string().nullable(),
  publishedAt: IsoTime.nullable(),
  publishedByPersonId: Uuid.nullable(),
  publishedByName: z.string().nullable(),
  /** Whether this is the version new tickets follow. */
  isCurrent: z.boolean(),
});
export type VersionSummary = z.infer<typeof VersionSummarySchema>;

/** A status as Ticket Core and the read side refer to it (DM-3: found by system code, never by name). */
export const StatusRefSchema = z.object({
  id: Uuid,
  name: z.string(),
  stageId: Uuid,
  stageCode: StageCodeSchema,
  stageName: z.string(),
  systemCode: StatusSystemCodeSchema.nullable(),
  isStageDefault: z.boolean(),
  shownToReporters: z.boolean(),
});
export type StatusRef = z.infer<typeof StatusRefSchema>;

// ---- Draft editing (S10, Platform admins, REQ-7.9) -------------------------------------------------

export const DraftChangeSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('add_status'),
    stage: StageCodeSchema,
    name: StatusName,
    /** For Not actioned outcomes: whether reporters see the outcome (REQ-9.2, OD-31). Default true. */
    shownToReporters: z.boolean().optional(),
  }),
  z.object({ op: z.literal('rename_status'), statusId: Uuid, name: StatusName }),
  /** The full order of one stage's statuses in the draft. The stage default stays the default. */
  z.object({ op: z.literal('reorder_statuses'), stage: StageCodeSchema, statusIds: z.array(Uuid).min(1).max(50) }),
  z.object({ op: z.literal('delete_status'), statusId: Uuid }),
  z.object({
    op: z.literal('add_transition'),
    fromStatusId: Uuid,
    toStatusId: Uuid,
    requiresReason: z.boolean().optional(),
    roles: z.array(TransitionRoleSchema).max(2).optional(),
  }),
  z.object({
    op: z.literal('update_transition'),
    transitionId: Uuid,
    requiresReason: z.boolean().optional(),
    roles: z.array(TransitionRoleSchema).max(2).optional(),
  }),
  z.object({ op: z.literal('remove_transition'), transitionId: Uuid }),
]);
export type DraftChange = z.infer<typeof DraftChangeSchema>;

export const PUBLISH_CHECK_CODES = [
  'stage_missing',
  'stage_default_missing',
  'system_status_missing',
  'protected_move_missing',
  'protected_move_narrowed',
  'transition_outside_version',
  'status_stage_mismatch',
] as const;
export const CheckFailureSchema = z.object({
  code: z.enum(PUBLISH_CHECK_CODES),
  message: z.string(),
});
export type CheckFailure = z.infer<typeof CheckFailureSchema>;

export const CheckResultSchema = z.object({ ok: z.boolean(), failures: z.array(CheckFailureSchema) });
export type CheckResult = z.infer<typeof CheckResultSchema>;

export const PublishDraftBodySchema = z.object({ changeSummary: z.string().trim().min(1).max(1000) });
export type PublishDraftBody = z.infer<typeof PublishDraftBodySchema>;

export const VersionParamsSchema = z.object({ id: Uuid });
